<template>
  <div>
    <div class="is-flex align-center">
      <div class="tabs is-toggle is-toggle-rounded is-marginless" style="overflow: initial">
        <ul>
          <li v-for="tab in tabs" :key="tab.key" :class="{ 'is-active': activeTab === tab.key }">
            <a @click="activateTab(tab.key)">
              {{ tab.label }}
              <span v-if="selectedCount(tab.key)" class="tag is-primary is-rounded is-small ml-1">
                {{ selectedCount(tab.key) }}
              </span>
            </a>
          </li>
        </ul>
      </div>
      <action-button
        v-if="hasAnySelection"
        class="is-light is-rounded ml-2"
        @click="clearAll">
        Clear filters
      </action-button>
    </div>

    <div v-if="activeTab" class="box p-3 mt-2">
      <div class="is-flex align-center mb-2">
        <text-input
          class="is-marginless flex-grow"
          :label="false"
          :placeholder="`Search ${activeTabDef.label.toLowerCase()}...`"
          v-model="search"
          @input="fetchItems"
          :debounce="300"
          left-icon="search">
        </text-input>
        <action-button
          v-if="selectedCount(activeTab)"
          class="is-light is-rounded ml-2"
          @click="clearTab(activeTab)">
          Clear
        </action-button>
      </div>

      <div style="max-height: 240px; overflow-y: auto">
        <div v-if="loadingItems" class="has-text-centered py-3">
          <icon icon="spinner" spin />
        </div>
        <p v-else-if="!items.length" class="has-text-centered has-text-grey py-2 is-size-7">
          No {{ activeTabDef.label.toLowerCase() }} found
        </p>
        <a
          v-else
          v-for="item in items"
          :key="item.id"
          class="is-flex align-center px-2 py-1 is-rounded"
          :class="isSelected(activeTab, item.id) ? 'has-background-primary-light' : 'is-hoverable'"
          @click="toggle(item)"
          style="cursor: pointer">
          <icon
            :icon="isSelected(activeTab, item.id) ? 'check-square' : 'square'"
            :type="isSelected(activeTab, item.id) ? 'fas' : 'far'"
            class="mr-2"
            :class="isSelected(activeTab, item.id) ? 'has-text-primary' : 'has-text-grey-light'" />
          <span>{{ getItemLabel(item) }}</span>
        </a>
      </div>

      <div v-if="selectedItems.length" class="mt-2 is-flex is-flex-wrap-wrap" style="gap: 4px">
        <span
          v-for="item in selectedItems"
          :key="item.id"
          class="tag is-primary is-rounded">
          {{ getItemLabel(item) }}
          <button class="delete is-small" @click.stop="deselect(item.id)" />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { site, zone, client, assetType, jobType } from '@/api'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({
        site_ids: [],
        zone_ids: [],
        client_ids: [],
        asset_type_ids: [],
        job_type_ids: [],
      })
    },
    disabled: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      activeTab: null,
      search: '',
      items: [],
      loadingItems: false,
      selectedCache: {
        sites: {},
        zones: {},
        clients: {},
        asset_types: {},
        job_types: {},
      }
    }
  },

  computed: {
    tabs() {
      return [
        { key: 'sites',       label: this.Naming.Sites,       valueKey: 'site_ids',       labelKey: 'name_and_city' },
        { key: 'zones',       label: this.Naming.Zones,       valueKey: 'zone_ids',       labelKey: 'name', labelFn: item => `${item.name} (${item.id})` },
        { key: 'clients',     label: this.Naming.Clients,     valueKey: 'client_ids',     labelKey: 'legal_name' },
        { key: 'asset_types', label: this.Naming.AssetTypes,  valueKey: 'asset_type_ids', labelKey: 'name' },
        { key: 'job_types',   label: 'Job Types',             valueKey: 'job_type_ids',   labelKey: 'description' },
      ]
    },

    activeTabDef() {
      return this.tabs.find(t => t.key === this.activeTab)
    },

    selectedItems() {
      if (!this.activeTab) return []
      return Object.values(this.selectedCache[this.activeTab])
    },

    hasAnySelection() {
      return this.tabs.some(t => (this.value[t.valueKey] || []).length > 0)
    },
  },

  methods: {
    activateTab(key) {
      if (this.activeTab === key) {
        this.activeTab = null
        return
      }
      this.activeTab = key
      this.search = ''
      this.fetchItems()
    },

    fetchItems() {
      if (!this.activeTab) return
      this.loadingItems = true
      this.items = []

      const params = { search: this.search || '' }
      const siteIds = this.value.site_ids || []

      const done = (response) => {
        this.items = response.data?.data ?? response.data ?? []
        this.loadingItems = false
      }
      const fail = () => { this.loadingItems = false }

      switch (this.activeTab) {
        case 'sites':
          site.searchSite({ ...params, limit: 50 }, done, fail)
          break
        case 'zones':
          zone.searchZones({ ...params, site_ids: siteIds, limit: 50 }, done, fail)
          break
        case 'clients':
          client.searchClient({ ...params, site_ids: siteIds, limit: 50 }, done, fail)
          break
        case 'asset_types':
          assetType.searchAssetTypes({ ...params, limit: 50 }, done, fail)
          break
        case 'job_types':
          jobType.searchJobTypes({ ...params, limit: 50 }, done, fail)
          break
      }
    },

    toggle(item) {
      const def = this.activeTabDef
      const ids = [...(this.value[def.valueKey] || [])]
      const idx = ids.indexOf(item.id)

      if (idx === -1) {
        ids.push(item.id)
        this.$set(this.selectedCache[this.activeTab], item.id, item)
      } else {
        ids.splice(idx, 1)
        this.$delete(this.selectedCache[this.activeTab], item.id)
      }

      this.$emit('input', { ...this.value, [def.valueKey]: ids })
    },

    deselect(id) {
      const def = this.activeTabDef
      const ids = (this.value[def.valueKey] || []).filter(x => x !== id)
      this.$delete(this.selectedCache[this.activeTab], id)
      this.$emit('input', { ...this.value, [def.valueKey]: ids })
    },

    clearTab(tabKey) {
      const def = this.tabs.find(t => t.key === tabKey)
      this.selectedCache[tabKey] = {}
      this.$emit('input', { ...this.value, [def.valueKey]: [] })
    },

    clearAll() {
      const cleared = {}
      this.tabs.forEach(t => {
        cleared[t.valueKey] = []
        this.selectedCache[t.key] = {}
      })
      this.$emit('input', { ...this.value, ...cleared })
    },

    isSelected(tabKey, id) {
      const def = this.tabs.find(t => t.key === tabKey)
      return (this.value[def.valueKey] || []).includes(id)
    },

    selectedCount(tabKey) {
      const def = this.tabs.find(t => t.key === tabKey)
      if (!def) return 0
      return (this.value[def.valueKey] || []).length
    },

    getItemLabel(item) {
      const def = this.activeTabDef
      if (def.labelFn) return def.labelFn(item)
      return item[def.labelKey] || item.name || item.id
    },
  },

  watch: {
    'value.site_ids'(newIds, oldIds) {
      if (JSON.stringify(newIds) !== JSON.stringify(oldIds)) {
        if (this.activeTab === 'zones' || this.activeTab === 'clients') {
          this.fetchItems()
        }
      }
    }
  }
}
</script>
